import { useMemo } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import { buildPaidOrdersChartData } from '@/lib/metrics-sales-repurchase-buckets';
import { type ChartGranularity } from '@/lib/chart-axis';
import type { RevenueChartResponseDto } from '@/generated/korza-api-client/types.gen';
import { useChartTooltip } from '@/lib/hooks/use-chart-tooltip';
import { usePartialBuckets } from '@/lib/hooks/use-partial-buckets';
import type { ChartRange } from '@/lib/partial-buckets';
import { MetricsBarChart } from '@/components/ui/charts/bar-chart';
import { MetricsChartBarSeries } from '@/components/ui/charts/chart-bar-series';
import { ChartClickHint } from '@/components/ui/charts/chart-click-hint';

type SalesChartPointDto = RevenueChartResponseDto['points'][number];

interface PaidOrdersChartProps {
  granularity: ChartGranularity;
  points: SalesChartPointDto[];
  /** The filter's period: hatches the partial first/last bucket (CHART-01). */
  range?: ChartRange;
  countAxisMax?: number;
  syncId?: string;
  syncMethod?: 'index' | 'value';
  /**
   * When provided, each period column is clickable; `bucketStart` is the
   * point's original api date.
   */
  onBucketClick?: (bucketStart: string) => void;
}

export function PaidOrdersChart({
  granularity,
  points,
  range,
  countAxisMax,
  syncId,
  syncMethod,
  onBucketClick,
}: PaidOrdersChartProps) {
  const { LL, locale } = useI18nContext();

  const rows = useMemo(
    () => buildPaidOrdersChartData(points, granularity, locale),
    [granularity, locale, points]
  );
  const partial = usePartialBuckets(rows, range, granularity);
  const chartData = useMemo(
    () =>
      rows.map((row, index) => ({
        ...row,
        partial: partial.indexes.includes(index),
      })),
    [rows, partial.indexes]
  );

  const handleBucketClick = onBucketClick
    ? (row: Record<string, unknown>) => {
        if (typeof row.date === 'string') onBucketClick(row.date);
      }
    : undefined;

  const tooltipContent = useChartTooltip({
    footer: onBucketClick ? <ChartClickHint /> : undefined,
    formatRows: (_label, payload) => [
      {
        label: LL.totalSales(),
        value: new Intl.NumberFormat(locale).format(
          (payload[0]?.value as number) ?? 0
        ),
      },
    ],
  });

  return (
    <MetricsBarChart
      data={chartData}
      partialLegend={partial.edges}
      syncId={syncId}
      syncMethod={syncMethod}
      yAxisDomain={countAxisMax !== undefined ? [0, countAxisMax] : [0, 'auto']}
      tooltipContent={tooltipContent}
      onBucketClick={handleBucketClick}
    >
      <MetricsChartBarSeries
        dataKey="count"
        partialIndexes={partial.indexes}
        partialLabels={partial.labels}
      />
    </MetricsBarChart>
  );
}
